import axios, { AxiosError } from "axios";

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  isNetworkError: boolean;
  isTimeout: boolean;
  isCancelled: boolean;
  originalError: unknown;
}

export function handleApiError(error: unknown): ApiError {
  if (axios.isCancel(error)) {
    return {
      message: "Request was cancelled",
      isNetworkError: false,
      isTimeout: false,
      isCancelled: true,
      originalError: error,
    };
  }

  if (error instanceof Error && ("code" in error || "response" in error || "isAxiosError" in error)) {
    const axiosError = error as AxiosError;
    const isNetworkError = !axiosError.response && !axiosError.request;
    const isTimeout = axiosError.code === "ECONNABORTED" || axiosError.code === "ETIMEDOUT";

    if (isNetworkError) {
      return {
        message: "Network error — please check your connection and try again",
        isNetworkError: true,
        isTimeout: false,
        isCancelled: false,
        originalError: error,
      };
    }

    if (isTimeout) {
      return {
        message: "Request timed out — please try again",
        isNetworkError: false,
        isTimeout: true,
        isCancelled: false,
        originalError: error,
      };
    }

    const status = axiosError.response?.status;
    const data = axiosError.response?.data as any;
    const serverMessage = data?.message || data?.error;

    if (status === 401) {
      return { message: "Session expired — please log in again", status, isNetworkError: false, isTimeout: false, isCancelled: false, originalError: error };
    }
    if (status === 403) {
      return { message: "You do not have permission to perform this action", status, isNetworkError: false, isTimeout: false, isCancelled: false, originalError: error };
    }
    if (status === 404) {
      return { message: "Resource not found", status, isNetworkError: false, isTimeout: false, isCancelled: false, originalError: error };
    }
    if (status === 409) {
      return { message: serverMessage || "Resource already exists", status, isNetworkError: false, isTimeout: false, isCancelled: false, originalError: error };
    }
    if (status === 422) {
      return { message: serverMessage || "Validation failed", status, isNetworkError: false, isTimeout: false, isCancelled: false, originalError: error };
    }
    if (status && status >= 500) {
      return { message: "Server error — please try again later", status, isNetworkError: false, isTimeout: false, isCancelled: false, originalError: error };
    }

    return {
      message: serverMessage || axiosError.message || "An error occurred",
      status,
      isNetworkError: false,
      isTimeout: false,
      isCancelled: false,
      originalError: error,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message || "An unexpected error occurred",
      isNetworkError: false,
      isTimeout: false,
      isCancelled: false,
      originalError: error,
    };
  }

  return {
    message: "An unexpected error occurred",
    isNetworkError: false,
    isTimeout: false,
    isCancelled: false,
    originalError: error,
  };
}

export function getErrorMessage(error: unknown): string {
  return handleApiError(error).message;
}

export function isNetworkError(error: unknown): boolean {
  return handleApiError(error).isNetworkError;
}

export function logError(error: unknown, context?: string) {
  if (process.env.NODE_ENV !== "production") {
    const prefix = context ? `[${context}]` : "[Error]";
    console.error(`${prefix}`, error);
    if (error instanceof Error && error.stack) {
      console.error(`${prefix} Stack:`, error.stack);
    }
  }
}
